import { useContext, useEffect, useState } from 'react';
import { Chip, Tooltip, Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import {
    CheckCircleOutline,
    CloudDownload,
    HighlightOff,
} from '@mui/icons-material';
import ReplayIcon from '@mui/icons-material/Replay';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import {
    useCrud,
    useInteractionsFiles,
    useLoader,
    useToggle,
} from '../../../lib/hooks';
import { AppContext, SttTable, SttTopControls } from '../../../sporttia/all';
import {
    composeFormData,
    formatPriceByLocale,
    fullName,
    getErrorMessage,
    safeFormatDate,
} from '../../../lib/utils';
import ModalParticipant from './ModalParticipant';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import colors from '../../../styles/Colors';
import SttMshipDialog from '../../../sporttia/dialogs/SttMshipDialog';
import DataphonePaymentDialog from '../../../components/dialogs/DataphonePaymentDialog';
import POSStripePaymentDialog from '../../../components/stripe/POSStripePaymentDialog';
import useEventsService from '../../../services/EventsService';
import constants from '../../../config/constants';
import type { Payment } from '../../../types/models/Payment';
import { PosPaymentParams } from '../../../types/payment';
import { PERMISSIONS } from '../../../lib/permissions';

type FormAnswer = {
    value: unknown;
};

type FormField = {
    id: string | number;
    name?: string;
    type?: string;
    answer?: FormAnswer;
};

type User = {
    id?: number;
    name?: string;
    surname1?: string;
    surname2?: string;
    fullName?: string;
    displayName?: string;
    mship?: { id: number };
};

type Participant = {
    id?: number;
    name?: string;
    surname1?: string;
    surname2?: string;
    displayName?: string;
    user: User;
    creator?: User;
    form?: FormField[];
    enrollment?: Payment;
    mobile?: string;
    email?: string;
    notes?: string;
    team?: { id: number; name?: string };
    admin?: boolean;
    paymentForm?: string;
};

type Category = {
    id: number;
    name: string;
};

type Event = {
    id: number;
    name: string;
    form?: FormField[];
    categories?: Category[];
};

type ParticipantsData = {
    count: number;
    rows: Participant[];
};

type Props = {
    event: Event;
};

export default function EventParticipants({
    event,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();

    const [participants, setParticipants, , Get] = useCrud<ParticipantsData>();
    const [category, setCategory] = useState<Category | undefined>(
        event.categories?.[0],
    );
    const [participant, setParticipant] = useState<Participant | null>(null);
    const [openMshipDialog, setOpenMshipDialog] = useState<boolean>(false);
    const [selectedMshipId, setSelectedMshipId] = useState<number | null>(null);
    const [filters, setFilters] = useState<{
        name: string;
        creatorName: string;
    }>({
        name: '',
        creatorName: '',
    });
    const [paymentId, setPaymentId] = useState<number | null>(null);

    const [trash, toggleTrash] = useToggle(() => {
        loadParticipants();
    });

    // POS (datafono)
    const [posPaymentParams, setPosPaymentParams] =
        useState<PosPaymentParams | null>(null);

    useEffect(() => {
        if (event?.categories) {
            setCategory(event.categories[0]);
        }
    }, [event]);

    useEffect(() => {
        if (category?.id) {
            loadParticipants();
        }
    }, [category]);

    function loadParticipants(
        params: { page?: number; rows?: number } = { page: 1, rows: 25 },
    ): void {
        Get(`/events/categories/${category?.id}/participants`, {
            ...params,
            ...filters,
            trash,
        }).then((result: unknown) => {
            const data = result as ParticipantsData;
            if (data.rows) {
                setParticipants(data);
            }
        });
    }

    function download(mode: 'pdf' | 'csv'): void {
        const type = mode === 'pdf' ? 'application/pdf' : 'text/csv';
        downloadFile(
            `/events/categories/${category?.id}/participants.${mode}`,
            type,
            { ...filters, trash },
            `${event.name}_${category?.name}_${t('Participants')}.${mode}`,
        );
    }

    function updateParticipants(payment: Payment): void {
        setParticipants({
            ...participants,
            rows: participants.rows.map((row: any) =>
                !row.enrollment || row.enrollment?.id !== paymentId
                    ? row
                    : { ...row, enrollment: payment },
            ),
        });
    }

    const eventsService = useEventsService();
    const updateParticipantMutation = eventsService.useUpdateParticipant({
        onSuccess: (response: unknown) => {
            const data = response as {
                participant?: Participant;
                tpv?: {
                    payment?: { paymentForm?: string };
                    price?: number;
                    concept?: string;
                    id?: number;
                    sc?: {
                        id?: number;
                        short?: string;
                        customer?: { name?: string; cif?: string };
                        address?: string;
                        phonePublic?: string;
                    };
                };
            };
            const { participant: participantResponse, tpv } = data;

            if (participantResponse) {
                setParticipant(null);
                loadParticipants();
            } else if (
                tpv?.payment?.paymentForm ===
                constants.payment.paymentForms.dataphoneConnected.name
            ) {
                // params for the POS dialog (datáfono físico)
                setPosPaymentParams({
                    amount: tpv.price!,
                    concept: tpv.concept!,
                    idTpv: tpv.id!,
                    sc: {
                        id: tpv?.sc?.id,
                        scName: tpv?.sc?.short,
                        entityName: tpv?.sc?.customer?.name,
                        address: tpv?.sc?.address,
                        cif: tpv?.sc?.customer?.cif,
                        phone: tpv?.sc?.phonePublic,
                    },
                    returnParams: undefined,
                });
            }
        },
        onError: (error: unknown) => {
            setParticipant(null);
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });
    const createEventCategoryParticipantMutation =
        eventsService.useCreateEventCategoryParticipant({
            onSuccess: (response: unknown) => {
                const data = response as {
                    participant?: Participant;
                    tpv?: {
                        payment?: { paymentForm?: string };
                        price?: number;
                        concept?: string;
                        id?: number;
                        sc?: {
                            id?: number;
                            short?: string;
                            customer?: { name?: string; cif?: string };
                            address?: string;
                            phonePublic?: string;
                        };
                    };
                };
                const { participant: participantResponse, tpv } = data;

                if (participantResponse) {
                    setParticipant(null);
                    loadParticipants();
                } else if (
                    tpv?.payment?.paymentForm ===
                    constants.payment.paymentForms.dataphoneConnected.name
                ) {
                    // params for the POS dialog (datáfono físico)
                    setPosPaymentParams({
                        amount: tpv.price!,
                        concept: tpv.concept!,
                        idTpv: tpv.id!,
                        sc: {
                            id: tpv?.sc?.id,
                            scName: tpv?.sc?.short,
                            entityName: tpv?.sc?.customer?.name,
                            address: tpv?.sc?.address,
                            cif: tpv?.sc?.customer?.cif,
                            phone: tpv?.sc?.phonePublic,
                        },
                        returnParams: undefined,
                    });
                }
            },
            onError: (error: unknown) => {
                cxt?.showMessage('E', getErrorMessage(error));
            },
        });
    const deleteParticipantMutation = eventsService.useDeleteParticipant({
        onSuccess: () => {
            setParticipant(null);
            loadParticipants();
        },
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    function saveParticipant(participantToSave: Participant): void {
        const {
            name,
            mobile,
            email,
            notes,
            user,
            team,
            admin,
            paymentForm,
            form,
        } = participantToSave;
        const idTeamExists = team && team.id && team.id !== 0;
        const params: Record<string, unknown> = {
            name,
            mobile: mobile || undefined,
            email: email || undefined,
            notes: notes || undefined,
            idUser: user?.id,
            paymentForm:
                paymentForm || paymentForm === '' ? paymentForm : undefined,
            idTeam: idTeamExists ? team.id : null,
            teamName: team && !idTeamExists ? team.name : null,
            admin: idTeamExists ? admin : null,
            form: form ? composeFormData(form as any) : null,
        };

        delete params.team;
        delete params.user;
        delete params.category;

        if (participantToSave.id) {
            const putParams = {
                name: params?.name,
                email: params?.email,
                notes: params?.notes,
                mobile: params?.mobile,
                admin: params?.admin,
                form: params?.form,
            };

            updateParticipantMutation.mutate({
                participantId: participantToSave.id,
                params: putParams,
            });
        } else {
            createEventCategoryParticipantMutation.mutate({
                categoryId: category!.id,
                params,
            });
        }
    }

    const [isMutationsLoading, mutationsLoader] = useLoader([
        updateParticipantMutation.status,
        createEventCategoryParticipantMutation.status,
        deleteParticipantMutation.status,
    ]);

    return (
        <>
            {mutationsLoader}

            <SttTopControls
                p={2}
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.EVENT.MODIFY,
                    onClick: () => {
                        setParticipant({
                            user: {
                                id: 0,
                                name: '',
                            },
                            mobile: '',
                            email: '',
                            notes: '',
                            form: event.form ? [...event.form] : [],
                        });
                    },
                }}
                menu={[
                    {
                        caption: `${t('Download')} PDF`,
                        onClick: () => download('pdf'),
                    },
                    {
                        caption: `${t('Download')} CSV`,
                        onClick: () => download('csv'),
                    },
                ]}
                menuIcon={<CloudDownload />}
                fields={
                    [
                        {
                            caption: t('Category'),
                            name: 'category',
                            type: 'select',
                            xs: 12,
                            sm: 12,
                            md: 5,
                            lg: 4,
                            value: category?.id as any,
                            options:
                                event.categories &&
                                event.categories.map((cat: any) => ({
                                    label: cat.name,
                                    value: cat.id,
                                })),
                            onChange: ({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setCategory(
                                    event.categories &&
                                        event.categories.find(
                                            (cat) => cat.id === value,
                                        ),
                                );
                            },
                        },
                        {
                            caption: t('Name'),
                            name: 'name',
                            type: 'text',
                            xs: 12,
                            sm: 6,
                            md: 3,
                            lg: 3,
                            value: filters.name as any,
                            onEnterKey: loadParticipants,
                        },
                        {
                            caption: t('RegisteredBy'),
                            name: 'creatorName',
                            type: 'text',
                            xs: 12,
                            sm: 6,
                            md: 3,
                            lg: 3,
                            value: filters.creatorName as any,
                            onEnterKey: loadParticipants,
                        },
                    ] as any
                }
                onChange={({
                    name,
                    value,
                }: {
                    name: string | undefined;
                    value: unknown;
                }) => {
                    if (!name) return;
                    setFilters({ ...filters, [name]: value as string });
                }}
                onFilter={loadParticipants}
                onToggleTrash={toggleTrash}
            />
            <SttTable
                autoload={false}
                onFetch={loadParticipants}
                data={participants}
                columns={[
                    {
                        title: t('Name'),
                        align: 'left',
                        type: 'link',
                        value: (row: Participant) =>
                            fullName({
                                name: row.name || row.user?.name,
                                surname1: row.surname1 || row.user?.surname1,
                                surname2: row.surname2 || row.user?.surname2,
                                fullName:
                                    row.displayName ||
                                    row.user?.fullName ||
                                    row.user?.displayName,
                            }),
                        onClick: (row: Participant) => setParticipant(row),
                    },
                    {
                        title: t('RegisteredBy'),
                        type: 'text',
                        align: 'left',
                        value: (row: Participant) =>
                            fullName({
                                name: row.creator?.name,
                                surname1: row.creator?.surname1,
                                surname2: row.creator?.surname2,
                                fullName:
                                    row.creator?.fullName ||
                                    row.creator?.displayName,
                            }),
                    },
                    {
                        title: t('Form'),
                        value: (row: Participant) =>
                            row.form &&
                            row.form.map(
                                (item: any) =>
                                    item.answer?.value && (
                                        <Tooltip
                                            key={item.id}
                                            arrow
                                            placement="top"
                                            title={item.name || ''}
                                        >
                                            <Chip
                                                size="small"
                                                label={(() => {
                                                    if (item.type === 'DATE') {
                                                        return safeFormatDate(
                                                            item.answer?.value,
                                                            'dd-MM-yyyy',
                                                            {
                                                                locale: getDateLocale(),
                                                            },
                                                        );
                                                    }

                                                    if (item.type !== 'CHECK') {
                                                        return item.answer
                                                            ?.value as React.ReactNode;
                                                    }

                                                    // En caso de tipo check, usar icono SI/NO (el estilo es una ñapilla para que salga centrado verticalmente)
                                                    if (item.answer.value) {
                                                        return (
                                                            <CheckCircleOutline
                                                                style={{
                                                                    position:
                                                                        'relative',
                                                                    top: 2,
                                                                    color: colors.green,
                                                                }}
                                                            />
                                                        );
                                                    }

                                                    return (
                                                        <HighlightOff
                                                            style={{
                                                                position:
                                                                    'relative',
                                                                top: 2,
                                                                color: colors.red,
                                                            }}
                                                        />
                                                    );
                                                })()}
                                            />
                                        </Tooltip>
                                    ),
                            ),
                    },
                    {
                        title: t('Inscription'),
                        value: (row: Participant) =>
                            row.enrollment && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        color:
                                            row.enrollment.status ===
                                            constants.payment.status.paid.name
                                                ? colors.green
                                                : colors.red,
                                    }}
                                >
                                    <div>
                                        {row.enrollment.paymentDate &&
                                            safeFormatDate(
                                                row.enrollment.paymentDate,
                                                'd MMM yyyy',
                                                { locale: getDateLocale() },
                                            )}
                                    </div>
                                    <Box mx={2}>
                                        {row.enrollment.paymentFormLang}
                                    </Box>
                                    {row.enrollment.status ===
                                    constants.payment.status.paid.name ? (
                                        <Link
                                            style={{ color: colors.green }}
                                            onClick={() =>
                                                setPaymentId(row.enrollment!.id)
                                            }
                                        >
                                            {formatPriceByLocale(
                                                row.enrollment.price,
                                                cxt?.sc?.city?.country
                                                    ?.currency,
                                            )}
                                        </Link>
                                    ) : (
                                        <Link
                                            style={{ color: colors.red }}
                                            onClick={() =>
                                                setPaymentId(row.enrollment!.id)
                                            }
                                        >
                                            {formatPriceByLocale(
                                                row.enrollment.price,
                                                cxt?.sc?.city?.country
                                                    ?.currency,
                                            )}
                                        </Link>
                                    )}

                                    {'refund' in row.enrollment &&
                                    row.enrollment.refund ? (
                                        <ReplayIcon fontSize="small" />
                                    ) : (
                                        ''
                                    )}
                                </div>
                            ),
                    },
                    {
                        title: '',
                        value: (row: Participant) => (
                            <PersonOutlineOutlinedIcon
                                fontSize="small"
                                onClick={() => {
                                    setOpenMshipDialog(true);
                                    setSelectedMshipId(
                                        row.user?.mship?.id || null,
                                    );
                                }}
                            />
                        ),
                    },
                ]}
            />

            {!isMutationsLoading && event && category && participant && (
                <ModalParticipant
                    item={
                        {
                            ...participant,
                            paymentForm: participant?.paymentForm || '',
                        } as any
                    }
                    event={event as any}
                    category={category as any}
                    onSave={(participantToSave: any) => {
                        setParticipant(participantToSave);
                        saveParticipant(participantToSave);
                    }}
                    onClose={() => {
                        setParticipant(null);
                    }}
                    onDelete={(participantToDelete: any) => {
                        deleteParticipantMutation.mutate({
                            participantId: participantToDelete.id! as number,
                        });
                    }}
                    disableFieldsForParticipantWithUser
                />
            )}

            {paymentId && (
                <PaymentModal
                    idPayment={paymentId}
                    onClose={() => setPaymentId(null)}
                    onPay={updateParticipants}
                    onRefund={updateParticipants}
                    onRecovered={updateParticipants}
                    onDeleted={updateParticipants}
                />
            )}

            <SttMshipDialog
                open={openMshipDialog}
                mshipId={selectedMshipId}
                onClose={() => {
                    setOpenMshipDialog(false);
                    loadParticipants();
                }}
            />

            {cxt?.sc?.sporttiaStripeLocationId && posPaymentParams !== null ? (
                <POSStripePaymentDialog
                    paymentData={{
                        amount: posPaymentParams.amount,
                        concept: posPaymentParams.concept,
                        idTpv: posPaymentParams.idTpv,
                    }}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        setParticipant(null);
                        loadParticipants();
                    }}
                />
            ) : (
                <DataphonePaymentDialog
                    open={posPaymentParams !== null}
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        setParticipant(null);
                        loadParticipants();
                    }}
                />
            )}
        </>
    );
}
